import { buildPublishView } from './publish-view'
import type { BoVersion } from './types'

export type DraftBannerState =
  | { kind: 'unknown'; changes: number }
  | { kind: 'nothingStaged'; changes: number }
  | { kind: 'needsApproval'; version: number; changes: number }
  | { kind: 'approved'; version: number; changes: number }
  | { kind: 'live'; version: number; changes: number }

/**
 * What the draft banner may claim. `undefined`/`null` versions mean loading or failed — the banner
 * says nothing about publish state rather than guessing, because guessing "not published" on a live
 * collection is the exact mistake this replaces.
 *
 * Approval comes from `buildPublishView` so the banner and the versions table can never disagree.
 */
export function draftBannerState(
  versions: BoVersion[] | undefined | null,
  changes: number,
): DraftBannerState {
  if (!versions) {
    return { kind: 'unknown', changes }
  }
  const view = buildPublishView(versions)
  if (view.live) {
    return { kind: 'live', version: view.live.n, changes }
  }
  if (view.rows.length === 0) {
    return { kind: 'nothingStaged', changes }
  }

  const newest = [...view.rows].sort((a, b) => b.version.n - a.version.n)[0]

  return {
    kind:
      newest.blockedReason === 'needsApproval' ? 'needsApproval' : 'approved',
    version: newest.version.n,
    changes,
  }
}

/**
 * The version an operator has already passed to review: the newest one not yet published, unless a
 * reviewer declined it — a declined version is dead, so re-staging is how the draft is re-offered.
 */
export function pendingStagedVersion(
  versions: BoVersion[] | undefined | null,
): number | undefined {
  if (!versions?.length) {
    return undefined
  }
  const newest = [...buildPublishView(versions).rows].sort(
    (a, b) => b.version.n - a.version.n,
  )[0]
  if (newest.isLive || newest.verdict === 'declined') {
    return undefined
  }

  return newest.version.n
}

export type ReviewAction =
  | { kind: 'pass' }
  | { kind: 'passed'; version: number }
  | { kind: 'nothingToPass' }

/**
 * What the review button may do. `changes` is measured against LIVE — what publishing would do to
 * players — while `staged` answers a different question: is any of it new since the last pass?
 * Staging identical content twice buys nothing, so that case is a disabled "Passed to review".
 */
export function reviewAction(
  changes: number,
  staged?: { version: number; matchesDraft: boolean },
): ReviewAction {
  if (changes === 0) {
    return { kind: 'nothingToPass' }
  }
  if (staged?.matchesDraft) {
    return { kind: 'passed', version: staged.version }
  }

  return { kind: 'pass' }
}
